import React, { useState } from "react";
import axios from "axios";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Field } from "@/components/consultation/Field";
import { PersonalFields } from "@/components/consultation/PersonalFields";
import { MeetingMethodPicker } from "@/components/consultation/MeetingMethodPicker";
import { SlotPicker } from "@/components/consultation/SlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  relationship: "",
  service_interest: "",
  location: "",
  senior_name: "",
  message: "",
  preferred_contact: "email",
  contact_method: "",
  slot_datetime: "",
};

function getMissingFields(form) {
  const missing = [];
  if (!form.full_name) missing.push("name");
  if (!form.email) missing.push("email");
  if (!form.phone) missing.push("phone");
  if (!form.relationship) missing.push("relationship");
  if (!form.service_interest) missing.push("service");
  if (!form.slot_datetime) missing.push("date & time");
  if (!form.contact_method) missing.push("meeting method");
  return missing;
}

const FeeSummary = () => (
  <div className="rounded-xl bg-[var(--gg-surface)] border border-[var(--gg-border)] p-5 flex items-center gap-4">
    <span className="w-11 h-11 rounded-full bg-[var(--gg-primary)]/10 flex items-center justify-center text-[var(--gg-primary)]">
      <CreditCard size={18} />
    </span>
    <div className="flex-1">
      <div className="text-xs uppercase tracking-[0.22em] text-[var(--gg-primary)] font-semibold">
        Initial consultation fee
      </div>
      <div className="font-serif text-2xl text-[var(--gg-deep)] mt-0.5">
        $5,200 JMD <span className="text-base text-[var(--gg-text-muted)] font-body ml-1">≈ $50 USD</span>
      </div>
      <div className="text-xs text-[var(--gg-text-muted)] mt-1">Charged in USD via secure Stripe checkout.</div>
    </div>
  </div>
);

export default function ConsultationForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = getMissingFields(form);
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(", ")}.`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/consultations`, form);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Payment link was not created. Please try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="consultation-section"
      className="py-24 md:py-28"
      style={{
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #FFE5D9 60%, #E8DCF3 100%)",
      }}
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[11px] tracking-[0.28em] uppercase text-[var(--gg-primary)] font-semibold">
            Initial Consultation · $5,200 JMD ≈ $50 USD
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-[54px] text-[var(--gg-deep)] mt-4 leading-[1.05] font-semibold text-balance">
            Begin with a conversation,
            <br />
            <em className="not-italic text-[var(--gg-primary)] font-normal">
              at a time that suits you.
            </em>
          </h2>
          <p className="mt-5 text-[var(--gg-text)] leading-relaxed">
            Pick your preferred day and time. Weekdays 9:30 AM – 3:30 PM
            (Jamaica time). Your slot is confirmed after payment.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white text-[var(--gg-text)] rounded-2xl p-8 md:p-12 shadow-xl shadow-[var(--gg-primary)]/10 border border-[var(--gg-border)] space-y-8"
          data-testid="consultation-form"
        >
          <PersonalFields form={form} update={update} />

          <MeetingMethodPicker
            value={form.contact_method}
            onChange={(v) => update("contact_method", v)}
          />

          <SlotPicker
            slot={form.slot_datetime}
            onSlotChange={(v) => update("slot_datetime", v)}
          />

          <Field label="Tell us more (optional)">
            <Textarea
              data-testid="form-message"
              rows={4}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="A few words about the senior's situation and what you're hoping for..."
            />
          </Field>

          <FeeSummary />

          <div>
            <button
              type="submit"
              data-testid="form-submit"
              disabled={submitting}
              className="inline-flex items-center gap-3 bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)] disabled:opacity-60 transition-colors rounded-full pl-7 pr-3 py-3.5 shadow-lg shadow-[var(--gg-primary)]/25"
            >
              {submitting ? "Redirecting to secure checkout…" : "Continue to secure checkout"}
              <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <ArrowRight size={16} />
              </span>
            </button>
            <p className="text-xs text-[var(--gg-text-muted)] mt-4">
              Your slot is held while you complete payment. You&apos;ll receive
              a confirmation email at <strong>{form.email || "your email"}</strong> once your payment
              is received.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
